import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  HStack,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import InvoiceList from '../components/InvoiceList';
import InvoiceModal from '../components/InvoiceModal';
import usePagination from '../utils/usePagination';
import api from '../api';

const SellInvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null); // For displaying invoice details
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const parseTotal = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const totalWithoutVat = parseTotal(selectedInvoice?.total_without_vat || 0);
  const vat = parseTotal(selectedInvoice?.vat || 0);
  const totalWithVat = parseTotal(selectedInvoice?.total_with_vat || 0);

  const baseUrl = '/sell-invoices/';

  const fetchInvoices = async (url = `${baseUrl}?page=1`) => {
    setLoading(true);
    try {
      const response = await api.get(url);
      setInvoices(response.data.results);
      updatePagination(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const {
    pagination,
    updatePagination,
    fetchNext,
    fetchPrevious,
    goToPage,
  } = usePagination(fetchInvoices, baseUrl);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleCreateClick = () => {
    setEditingInvoice(null);
    setIsCreateModalOpen(true);
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`${baseUrl}${invoiceId}/`); // Fetch the latest invoice data
      setEditingInvoice({
        ...response.data,
        total_without_vat: response.data.total_without_vat || 0,
        vat: response.data.vat || 0,
        total_with_vat: response.data.total_with_vat || 0,
      });
      setIsCreateModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch updated invoice data:', error);
    }
  };
  

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleDeleteInvoice = async (id) => {
    if (!id) return;

    try {
      await api.delete(`${baseUrl}${id}/`);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
    }
  };

  const handleViewDetails = async (invoiceId) => {
    try {
      const response = await api.get(`${baseUrl}${invoiceId}/`);
      setSelectedInvoice(response.data);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
    }
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedInvoice(null);
  };

  const renderInvoiceForPrint = (invoiceData) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>Накладна</title></head><body style="font-family: Arial, sans-serif; font-size: 12px;">');
    printWindow.document.write('<h1 style="text-align: center; margin-bottom: 20px; font-size: 18px;">Накладна #' + invoiceData.id + '</h1>');
    printWindow.document.write('<table border="1" cellpadding="5" cellspacing="0" style="width: 100%; border-collapse: collapse; border-color: #ddd;">');
    printWindow.document.write('<tr><td colspan="5" style="background-color: #f0f0f0; border-bottom: 1px solid #ddd; font-size: 14px;">');
    printWindow.document.write('<p style="margin-bottom: 10px;">Дата створення: ' + new Date(invoiceData.created_at).toLocaleString() + '</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Примітка: ' + (invoiceData.note || 'Без приміток') + '</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Клієнт: ' + (invoiceData.customer_name || 'Без клієнта') + '</p>');
    printWindow.document.write('</td></tr>');
    printWindow.document.write('<tr style="background-color: #f0f0f0; border-bottom: 1px solid #ddd;"><th style="width: 10%;">№</th><th style="width: 40%;">Назва товару</th><th style="width: 15%;">Ціна</th><th style="width: 15%;">Кількість</th><th style="width: 20%;">Сума</th></tr>');
    
    invoiceData.entries.forEach((entry, index) => {
      const price = parseFloat(entry.price);
      printWindow.document.write(
        `<tr style="border-bottom: 1px solid #ddd;"><td style="text-align: center;">${index + 1}</td><td>${entry.product.name}</td><td style="text-align: right;">${price.toFixed(2)} грн.</td><td style="text-align: center;">${entry.quantity}</td><td style="text-align: right;">${(price * entry.quantity).toFixed(2)} грн.</td></tr>`
      );
    });
    
    printWindow.document.write('</table>');
    printWindow.document.write('<div style="float: right; margin-top: 20px;">');
    printWindow.document.write('<p style="margin-bottom: 10px;">Разом без ПДВ: ' + invoiceData.total_without_vat + ' грн.</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">ПДВ: ' + invoiceData.vat + ' грн.</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Всього з ПДВ: ' + invoiceData.total_with_vat + ' грн.</p>');
    printWindow.document.write('</div>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };
  
  const handlePrintInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`${baseUrl}${invoiceId}/`);
      renderInvoiceForPrint(response.data);
    } catch (error) {
      console.error('Failed to print invoice:', error);
    }
  };

  const handleChangeStatus = async (invoiceId, newStatus) => {
    try {
      await api.patch(`${baseUrl}${invoiceId}/`, { status: newStatus });
      fetchInvoices(); // Refresh invoices after status change
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  const handleInvoiceCreated = async (newInvoice) => {
    // Optimistically update the UI
    setInvoices((prevInvoices) => [newInvoice, ...prevInvoices]);
  
    try {
      const response = await api.get(`/sell-invoices/${newInvoice.id}/`);
      setInvoices((prevInvoices) =>
        prevInvoices.map((invoice) =>
          invoice.id === newInvoice.id ? response.data : invoice
        )
      );
    } catch (error) {
      console.error('Failed to sync invoice:', error);
    }
  };  

  return (
    <Box p="6">
      {/* Header */}
      <Flex justify="space-between" align="center" mb="4">
        <Text fontSize="2xl" fontWeight="bold">
          Накладні продажу
        </Text>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon />}
          onClick={handleCreateClick}
        >
          Створити накладну
        </Button>
      </Flex>

      {/* Invoices List */}
      {loading ? (
        <Spinner />
      ) : invoices.length ? (
        <Box>
          <InvoiceList
            invoices={invoices}
            onEdit={handleEditInvoice}
            onDelete={handleDeleteInvoice}
            onViewDetails={handleViewDetails}
            onPrintInvoice={handlePrintInvoice}
            onChangeStatus={handleChangeStatus}
          />
        </Box>
      ) : (
        <Text>Немає доступних накладних.</Text>
      )}

      {/* Pagination */}
      {pagination.count > 0 && (
        <Flex justify="center" mt="6" align="center">
          <HStack spacing="2">
            <Button
              size="sm"
              onClick={fetchPrevious}
              isDisabled={!pagination.previous}
            >
              Назад
            </Button>
            {Array.from(
              { length: Math.ceil(pagination.count / 10) },
              (_, index) => (
                <Button
                  key={index + 1}
                  size="sm"
                  colorScheme={
                    pagination.currentPage === index + 1 ? 'blue' : 'gray'
                  }
                  onClick={() => goToPage(index + 1)}
                >
                  {index + 1}
                </Button>
              )
            )}
            <Button
              size="sm"
              onClick={fetchNext}
              isDisabled={!pagination.next}
            >
              Вперед
            </Button>
          </HStack>
        </Flex>
      )}

      {/* Create/Edit Modal */}
      <InvoiceModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        type={"sell"}
        invoiceData={editingInvoice}
        onInvoiceCreated={handleInvoiceCreated}
      />

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <Modal
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          size="4xl" // Bigger modal size
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Деталі накладної</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
            <Box>
              <Text fontWeight="bold" mb="4">Накладна #{selectedInvoice.id}</Text>
              <Text>Дата створення: {new Date(selectedInvoice.created_at).toLocaleString()}</Text>
              <Text>Клієнт: {selectedInvoice.customer_name || 'Без клієнта'}</Text>
              <Text mb="4">Примітка: {selectedInvoice.note || 'Без приміток'}</Text>
                {/* Scrollable Product List */}
                <Box
                  maxH="400px" // Fixed height
                  overflowY="auto" // Enable vertical scroll
                  borderWidth="1px"
                  borderRadius="md"
                  p="4"
                >
                  <Table variant="striped" colorScheme="gray" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Назва товару</Th>
                        <Th>Закупочна ціна</Th>
                        <Th>Відсоток націнки</Th>
                        <Th>Ціна за одиницю</Th>
                        <Th>Кількість</Th>
                        <Th>Загальна ціна</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {selectedInvoice.entries.map((entry) => (
                        <Tr key={entry.id}>
                          <Td>{entry.product.name}</Td>
                          <Td>{entry.purchase_price} грн</Td>
                          <Td>{entry.markup_percentage} %</Td>
                          <Td>{entry.price} грн</Td>
                          <Td>{entry.quantity}</Td>
                          <Td>{entry.price * entry.quantity} грн</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                {/* Totals */}
                <Box textAlign="right" mt="4" borderTop="1px solid #e2e8f0" pt="4">
                  <Text>Разом без ПДВ: {totalWithoutVat.toFixed(2)} грн</Text>
                  <Text>ПДВ: {selectedInvoice.entries.some((entry) => entry.product.is_vat) ? vat.toFixed(2) : 0} грн</Text>
                  <Text fontWeight="bold">
                    Всього з ПДВ: {selectedInvoice.entries.some((entry) => entry.product.is_vat) ? totalWithVat.toFixed(2) : totalWithoutVat.toFixed(2)} грн
                  </Text>
                </Box>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

    </Box>
  );
};

export default SellInvoicesPage;
